import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Mail, RefreshCw, Send } from 'lucide-react'
import type { components } from '@/api/schema'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { LoadingButton } from '@/components/loading-button'
import { formatEventTime } from '@/lib/utils'
import { ComposeTextarea } from '@/components/compose-field'
import { EmailRecipientsField } from '@/components/email-recipients-field'
import { type EmailRecipientsOverride, useEmailRecipients } from '@/components/use-email-recipients'

export type ActionCustomerEmail = components['schemas']['ActionCustomerEmailRead']

const SENT_STATUSES = new Set<ActionCustomerEmail['status']>(['sent', 'awaiting_customer', 'customer_replied'])

/** The body followed by every line staff left shown. */
function composeCustomerEmail(body: string, parts: readonly { text: string }[]) {
  return [body.trim(), ...parts.map((part) => part.text)].filter(Boolean).join('\n\n')
}

/**
 * The customer email an action item sends, opened only when staff choose to email (USA-1413). It looks and
 * works like the card's reply composer: the draft, the optional lines (new ETA, offered model…) to include,
 * then Send or Cancel. It goes in the order's customer thread, so it has no subject.
 */
export function CustomerEmailComposer({
  actionId,
  email,
  recipient,
  recipientEmail,
  onSend,
  sending,
  onCancel,
  onRegenerate,
  regenerating = false,
  disabled = false,
}: {
  actionId: number
  email: ActionCustomerEmail
  recipient?: string | null
  /** The customer's address, the default To that staff can change for this email (USA-1663). */
  recipientEmail?: string | null
  onSend: (bodyText: string, recipients: EmailRecipientsOverride) => void
  sending: boolean
  onCancel: () => void
  onRegenerate?: () => void
  regenerating?: boolean
  disabled?: boolean
}) {
  const [body, setBody] = useState(email.body)
  const recipients = useEmailRecipients({ to: recipientEmail ? [recipientEmail] : [], cc: [] })
  const parts = email.parts ?? []
  const [hidden, setHidden] = useState<Set<string>>(
    () => new Set(parts.filter((part) => !part.shown).map((part) => part.key)),
  )
  const composed = composeCustomerEmail(
    body,
    parts.filter((part) => !hidden.has(part.key)),
  )
  const busy = disabled || sending || regenerating
  const fieldId = `customer-email-${actionId}`

  return (
    <div className="space-y-3 rounded-md border bg-muted/30 p-3">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Mail className="h-4 w-4" aria-hidden />
        <p className="text-sm font-medium">Email customer</p>
      </div>
      <EmailRecipientsField
        state={recipients}
        toName={recipient !== recipientEmail ? recipient : null}
        fallback={recipient || "The order's customer"}
        disabled={busy}
      />
      <div className="space-y-1">
        <Label htmlFor={fieldId}>Email</Label>
        <ComposeTextarea
          id={fieldId}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          rows={6}
          className="min-h-[120px]"
          autoFocus
        />
      </div>
      {parts.length > 0 && (
        <fieldset className="space-y-1.5">
          <legend className="sr-only">Lines to include</legend>
          {parts.map((part) => (
            <label key={part.key} className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1 accent-foreground"
                checked={!hidden.has(part.key)}
                onChange={(event) =>
                  setHidden((current) => {
                    const next = new Set(current)
                    if (event.target.checked) next.delete(part.key)
                    else next.add(part.key)
                    return next
                  })
                }
              />
              <span className={hidden.has(part.key) ? 'text-muted-foreground line-through' : undefined}>
                {part.text}
              </span>
            </label>
          ))}
        </fieldset>
      )}
      <div className="flex flex-wrap gap-2">
        <LoadingButton
          size="sm"
          loading={sending}
          loadingText="Sending..."
          disabled={busy || !composed.trim()}
          onClick={() => onSend(composed, recipients.override)}
        >
          <Send className="mr-1 h-3 w-3" /> Send to customer
        </LoadingButton>
        {onRegenerate && (
          <LoadingButton
            size="sm"
            variant="outline"
            loading={regenerating}
            loadingText="Writing..."
            disabled={busy}
            onClick={onRegenerate}
          >
            <RefreshCw className="mr-1 h-3 w-3" /> Rewrite
          </LoadingButton>
        )}
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={sending}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

/** A held automatic email's message, opened read-only from the card's Preview button. */
export function CustomerEmailPreview({ email }: { email: ActionCustomerEmail }) {
  return <p className="whitespace-pre-line rounded-md bg-muted/50 px-3 py-2 text-sm">{email.body}</p>
}

/** One quiet line once the customer has been emailed; the email itself lives in the conversation. */
export function CustomerEmailSentLine({ email }: { email: ActionCustomerEmail }) {
  if (!SENT_STATUSES.has(email.status)) return null
  return (
    <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
      <span>
        {email.status === 'customer_replied' ? 'Customer replied' : 'Emailed customer'}
        {email.sent_at && ` · ${formatEventTime(email.sent_at)}`}
      </span>
      {email.thread_id != null && (
        <>
          <span aria-hidden>·</span>
          <Link
            to="/customer-comms/$threadId"
            params={{ threadId: String(email.thread_id) }}
            search={email.message_id != null ? { message: String(email.message_id) } : {}}
            className="font-medium text-foreground underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-foreground"
          >
            Open the conversation →
          </Link>
        </>
      )}
    </p>
  )
}
